import { Navigate, Route, Routes } from "react-router-dom";

import { AdminShell } from "src/components/shell/AdminShell";
import { SignInGate } from "src/components/editorial/SignInGate";
import { StreamLayout } from "src/components/editorial/StreamLayout";
import { AlertsWorkspace } from "src/workspaces/AlertsWorkspace";
import { AlertHistoryWorkspace } from "src/workspaces/AlertHistoryWorkspace";
import { RuleFormWorkspace } from "src/workspaces/RuleFormWorkspace";
import { BroadcastsWorkspace } from "src/workspaces/BroadcastsWorkspace";
import { ChannelsWorkspace } from "src/workspaces/ChannelsWorkspace";
import { CostsWorkspace } from "src/workspaces/CostsWorkspace";
import { FeedItemsWorkspace } from "src/workspaces/FeedItemsWorkspace";
import { NewFeedSourceWorkspace } from "src/workspaces/NewFeedSourceWorkspace";
import { FeedSourcesWorkspace } from "src/workspaces/FeedSourcesWorkspace";
import { TrendDetailWorkspace } from "src/workspaces/TrendDetailWorkspace";
import { TrendsWorkspace } from "src/workspaces/TrendsWorkspace";
import { ClaimThreadWorkspace } from "src/workspaces/ClaimThreadWorkspace";
import { ClaimsWorkspace } from "src/workspaces/ClaimsWorkspace";
import { ComparisonWorkspace } from "src/workspaces/ComparisonWorkspace";
import { FlowWorkspace } from "src/workspaces/FlowWorkspace";
import { NotesWorkspace } from "src/workspaces/NotesWorkspace";
import { ProcessingWorkspace } from "src/workspaces/ProcessingWorkspace";
import { ReaderWorkspace } from "src/workspaces/ReaderWorkspace";
import { OverviewWorkspace } from "src/workspaces/OverviewWorkspace";
import { RelevanceWorkspace } from "src/workspaces/RelevanceWorkspace";
import { StoriesWorkspace } from "src/workspaces/StoriesWorkspace";
import { StreamAlertsWorkspace } from "src/workspaces/StreamAlertsWorkspace";
import { TopicsWorkspace } from "src/workspaces/TopicsWorkspace";
import { TranscriptWorkspace } from "src/workspaces/TranscriptWorkspace";
import { VerificationWorkspace } from "src/workspaces/VerificationWorkspace";
import { VoiceProfileWorkspace } from "src/workspaces/VoiceProfileWorkspace";
import { PeopleWorkspace } from "src/workspaces/PeopleWorkspace";
import { VoiceProfilesWorkspace } from "src/workspaces/VoiceProfilesWorkspace";

/**
 * Every route is behind the gate. Unpublished drafts, raw transcripts and voice recordings of
 * identifiable people are the whole content of this dashboard; none of it has a public reading.
 */
export function App() {
  return (
    <SignInGate>
      <Routes>
        {/* Every route sits inside the shell; the reader opts out from within it, so the exception
            is declared in one place instead of by each route remembering to. */}
        <Route element={<AdminShell />}>
          <Route path="/" element={<BroadcastsWorkspace />} />
          <Route path="/stream/:streamId" element={<StreamLayout />}>
            <Route index element={<OverviewWorkspace />} />
            {/* Keep old stream-scoped links useful while the flow guide lives only in Cómo funciona. */}
            <Route path="flujo" element={<Navigate to="/flujo" replace />} />
            <Route path="transcript" element={<TranscriptWorkspace />} />
            <Route path="topics" element={<TopicsWorkspace />} />
            <Route path="claims" element={<ClaimsWorkspace />} />
            <Route path="relevance" element={<RelevanceWorkspace />} />
            <Route path="stories" element={<StoriesWorkspace />} />
            <Route path="verification" element={<VerificationWorkspace />} />
            <Route path="notes" element={<NotesWorkspace />} />
            <Route path="alertas" element={<StreamAlertsWorkspace />} />
            <Route path="claim/:claimId" element={<ClaimThreadWorkspace />} />
          </Route>
          {/* Asking the pipeline for work belongs to no single stream either: the video being
            submitted does not have one yet. */}
          <Route path="/procesar" element={<ProcessingWorkspace />} />
          {/* Cross-stream, so it sits beside the broadcast list rather than inside a stream. */}
          <Route path="/alertas" element={<AlertsWorkspace />} />
          <Route path="/alertas/nueva" element={<RuleFormWorkspace />} />
          <Route path="/alertas/:ruleId/editar" element={<RuleFormWorkspace />} />
          <Route path="/alertas/historial" element={<AlertHistoryWorkspace />} />

          <Route path="/tendencias" element={<TrendsWorkspace />} />
          <Route path="/tendencias/:topicId" element={<TrendDetailWorkspace />} />
          {/* The process explained on its own, for someone who has not opened a broadcast yet. */}
          <Route path="/flujo" element={<FlowWorkspace />} />
          {/* Voices are configuration, not a broadcast — they outlive any single stream, so they sit
            outside StreamLayout rather than under one. */}
          <Route path="/voces" element={<VoiceProfilesWorkspace />} />
          <Route path="/voces/:profileId" element={<VoiceProfileWorkspace />} />
          {/* Which channels the box watches — configuration that outlives any single broadcast,
            same reasoning as voices. */}
          <Route path="/origenes" element={<ChannelsWorkspace />} />
          {/* What the machine costs to run. Cross-stream like Voces and Orígenes: the question is
            about the operation, not about one broadcast — though the table below breaks it down
            per mañanera. */}
          <Route path="/costos" element={<CostsWorkspace />} />
          {/* The second way information gets in: sites we read rather than broadcasts we listen
            to. The registry is configuration, like Orígenes; the posts are what it brought in. */}
          <Route path="/suscripciones" element={<FeedSourcesWorkspace />} />
          {/* Its own screen, like a new alert rule: verifying an address has a result worth
            reading before anything is written, which a row in a form cannot hold. */}
          <Route path="/suscripciones/nueva" element={<NewFeedSourceWorkspace />} />
          <Route path="/publicaciones" element={<FeedItemsWorkspace />} />
          {/* Who may enter. Drawn in the rail only for an admin, and refused by the API for anyone
            else — the second is what enforces it. */}
          <Route path="/personas" element={<PeopleWorkspace />} />
          {/* The reader lives outside StreamLayout on purpose: it is a full escape from the review
            chrome. The paths match the legacy dashboard's, so existing links keep working. */}
          <Route
            path="/nota/:streamId/:transformationId"
            element={<ReaderWorkspace />}
          />
          <Route
            path="/nota/:streamId/:transformationId/comparar"
            element={<ComparisonWorkspace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </SignInGate>
  );
}
